/**
 * KeyboardDock — the on-screen control area, docked at the bottom of the
 * app shell. Always expanded; previous strip / hidden modes were removed
 * because the user prefers the section to be permanently visible.
 *
 * Three tabs share the same dock footprint, swapped by the Kbd / Arp /
 * Seq buttons:
 *   - piano — full two-octave keyboard surface
 *   - arp   — arpeggiator controls
 *   - seq   — step sequencer panel
 *
 * The seq tab is the default on first launch (most users open the app
 * to sequence, not to play live). The active tab persists in
 * localStorage along with octave / velocity / sustain so a reload
 * restores whatever the user had up.
 *
 * QWERTY key capture is focus-aware: typing in a text input won't play
 * notes. The keyboard never owns note state — it's pure input; all it
 * does is call back into onNoteOn/onNoteOff, which the app wires to the
 * VoiceManager.
 */

import { isBlackKey, midiToName, NOTE_NAMES } from '@ac-slayer/synth-core';

export interface KeyboardCallbacks {
  readonly onNoteOn: (note: number, velocity: number) => void;
  readonly onNoteOff: (note: number) => void;
  /** Optional — called on the preset-aware "Test" button. */
  readonly onTest?: () => void;
  /**
   * Optional pitch bend driver. `amount` is normalized: -1 = full bend down,
   * 0 = no bend, +1 = full bend up. Called on arrow-key down (±1) and up (0).
   */
  readonly onPitchBend?: (amount: number) => void;
}

/** Which sub-panel of the dock is currently visible. */
export type KeyboardTab = 'piano' | 'arp' | 'seq' | 'song' | 'atom';

interface KeyboardState {
  /** Which tab is active. Persisted across reloads so the user lands
   *  back where they left off. */
  activeTab: KeyboardTab;
  octave: number; // middle octave (the lower visible row)
  velocity: number; // 0..1
  sustain: boolean;
}

const STORAGE_KEY = 'evil-synth:keyboard';

const DEFAULT_STATE: KeyboardState = {
  // Sequencer wins the default — the synth is built around it for most
  // workflows and the user wants it visible on first paint.
  activeTab: 'seq',
  octave: 4, // C4 = middle C by the common convention
  velocity: 0.8,
  sustain: false,
};

/**
 * QWERTY-to-semitone-offset mapping (Ableton-style, two rows).
 * Lower row (Z/X/...): starts at the current octave's C.
 * Upper row (Q/2/...): starts at the octave above.
 */
const QWERTY_MAP: Record<string, number> = {
  // Lower row — octave = current
  KeyZ: 0, // C
  KeyS: 1, // C#
  KeyX: 2, // D
  KeyD: 3, // D#
  KeyC: 4, // E
  KeyV: 5, // F
  KeyG: 6, // F#
  KeyB: 7, // G
  KeyH: 8, // G#
  KeyN: 9, // A
  KeyJ: 10, // A#
  KeyM: 11, // B
  Comma: 12, // C (next octave)
  KeyL: 13, // C#
  Period: 14, // D
  Semicolon: 15, // D#
  Slash: 16, // E
  // Upper row — octave = current + 1
  KeyQ: 12,
  Digit2: 13,
  KeyW: 14,
  Digit3: 15,
  KeyE: 16,
  KeyR: 17,
  Digit5: 18,
  KeyT: 19,
  Digit6: 20,
  KeyY: 21,
  Digit7: 22,
  KeyU: 23,
  KeyI: 24,
  Digit9: 25,
  KeyO: 26,
  Digit0: 27,
  KeyP: 28,
};

/** Number of octaves shown in the expanded piano surface. */
const VISIBLE_OCTAVES = 2;

export class KeyboardDock {
  readonly element: HTMLElement;

  private state: KeyboardState;
  private readonly cb: KeyboardCallbacks;

  private readonly dock: HTMLElement;
  /** The keyboard tab's wrapper. Contains its own controls row (vel +
   *  sustain) on top of the piano keys, mirroring how the arp / seq
   *  tabs each render their own controls + body. Toggling visibility
   *  hides both halves at once. */
  private readonly keyboardTabEl: HTMLElement;
  private readonly pianoEl: HTMLElement;
  private readonly octaveLabel: HTMLElement;
  private readonly velocitySlider: HTMLInputElement;
  private readonly sustainBtn: HTMLButtonElement;
  private readonly kbdBtn: HTMLButtonElement;
  private readonly arpBtn: HTMLButtonElement;
  private readonly arpBadge: HTMLElement;
  private readonly arpSlot: HTMLElement;
  private readonly seqBtn: HTMLButtonElement;
  private readonly seqBadge: HTMLElement;
  private readonly seqSlot: HTMLElement;
  private readonly songBtn: HTMLButtonElement;
  private readonly songBadge: HTMLElement;
  private readonly songSlot: HTMLElement;
  private readonly atomBtn: HTMLButtonElement;
  private readonly atomSlot: HTMLElement;
  private readonly testBtn: HTMLButtonElement | null;
  /** Center slot inside the controls row. main.ts moves the topbar's
   *  Panic / Volume / Meter / Record cluster here after construction
   *  so the global transport controls live in the same toolbar as
   *  the tab buttons and BPM/MIDI picker. */
  private readonly controlsCenter: HTMLElement;
  /** Right-aligned slot inside the controls row. main.ts moves the
   *  BPM cluster and the MIDI picker here after construction so they
   *  share the same toolbar as the tab buttons. */
  private readonly controlsRight: HTMLElement;
  /** Active "tab" — only one of piano / arp / seq is visible at a time.
   *  Mirrors `state.activeTab` and is updated through `setActiveTab`. */
  private activeTab: KeyboardTab;
  /** Subscribers fired whenever the active tab changes. main.ts uses
   *  this to auto-enable the arp / sequencer when their tab is up. */
  private readonly tabListeners = new Set<(tab: KeyboardTab) => void>();

  /** Notes currently held (user hasn't released yet), before sustain. */
  private readonly physicallyHeld = new Set<number>();
  /** Notes currently sounding (after sustain filtering). */
  private readonly sounding = new Set<number>();
  /** Currently-held QWERTY codes (to suppress auto-repeat). */
  private readonly keysHeld = new Set<string>();

  private keyEls = new Map<number, HTMLElement>();

  constructor(cb: KeyboardCallbacks) {
    this.cb = cb;
    this.state = loadState();
    this.activeTab = this.state.activeTab;

    this.element = document.createElement('div');
    this.element.className = 'keyboard-root';

    // The dock is always visible — no collapse / strip / hidden modes.
    // Tabs swap which sub-panel (piano / arp / seq) takes the slot.
    this.dock = document.createElement('section');
    this.dock.className = 'keyboard-dock';
    this.dock.setAttribute('role', 'group');
    this.dock.setAttribute('aria-label', 'Keyboard, arpeggiator, and sequencer');

    const controls = document.createElement('div');
    controls.className = 'keyboard-controls';

    const octCtl = document.createElement('div');
    octCtl.className = 'keyboard-octave';
    octCtl.innerHTML = `<span class="label">Oct</span>`;
    const octDown = makeBtn('-', 'btn-sm', () => this.setOctave(this.state.octave - 1));
    this.octaveLabel = document.createElement('span');
    this.octaveLabel.className = 'mono keyboard-octave-val';
    const octUp = makeBtn('+', 'btn-sm', () => this.setOctave(this.state.octave + 1));
    octCtl.append(octDown, this.octaveLabel, octUp);

    const velCtl = document.createElement('label');
    velCtl.className = 'keyboard-velocity';
    velCtl.innerHTML = `<span class="label">Vel</span>`;
    this.velocitySlider = document.createElement('input');
    this.velocitySlider.type = 'range';
    this.velocitySlider.className = 'range-themed';
    this.velocitySlider.min = '0';
    this.velocitySlider.max = '1';
    this.velocitySlider.step = '0.01';
    this.velocitySlider.value = String(this.state.velocity);
    this.velocitySlider.addEventListener('input', () => {
      this.state.velocity = parseFloat(this.velocitySlider.value);
      saveState(this.state);
    });
    velCtl.appendChild(this.velocitySlider);

    this.sustainBtn = makeBtn('Sustain', 'btn-sm', () => this.toggleSustain());

    // Tab buttons — Kbd / Arp / Seq pick which sub-panel is visible
    // inside the dock. They behave exclusively (only one panel shows
    // at a time) and the active tab persists across reloads.
    this.kbdBtn = makeBtn('Kbd', 'btn-sm', () => this.setActiveTab('piano'));
    this.kbdBtn.title = 'Show the on-screen keyboard';

    // Arp tab toggle. main.ts calls setArpElement() once the arp panel
    // is built; until then the slot is empty. Clicking re-shows the
    // keyboard if the tab is already active (toggle behavior).
    this.arpBtn = makeBtn('Arp', 'btn-sm', () => this.toggleArp());
    this.arpBtn.title = 'Show the arpeggiator controls';
    // Small LED-style badge inside the Arp button. Lights up when the
    // arp is *actually enabled* (arp.enabled === 'on'), independent of
    // whether the tab is currently showing its panel. main.ts toggles
    // the 'on' class via setArpEnabledIndicator().
    const arpBadge = document.createElement('span');
    arpBadge.className = 'keyboard-arp-badge';
    arpBadge.setAttribute('aria-hidden', 'true');
    this.arpBtn.appendChild(arpBadge);
    this.arpBadge = arpBadge;

    // Seq tab toggle — same pattern as Arp. The badge lights when
    // seq.enabled === 'on'.
    this.seqBtn = makeBtn('Seq', 'btn-sm', () => this.toggleSeq());
    this.seqBtn.title = 'Show the step sequencer';
    const seqBadge = document.createElement('span');
    seqBadge.className = 'keyboard-arp-badge';
    seqBadge.setAttribute('aria-hidden', 'true');
    this.seqBtn.appendChild(seqBadge);
    this.seqBadge = seqBadge;

    // Song tab toggle — chains saved patterns into a song-level
    // arrangement. Badge lights when the song sequencer is playing.
    this.songBtn = makeBtn('Song', 'btn-sm', () => this.toggleSong());
    this.songBtn.title = 'Show the song sequencer (chain saved patterns)';
    const songBadge = document.createElement('span');
    songBadge.className = 'keyboard-arp-badge';
    songBadge.setAttribute('aria-hidden', 'true');
    this.songBtn.appendChild(songBadge);
    this.songBadge = songBadge;

    // Atom tab toggle — element-driven modulation source. Periodic
    // table picker, chord trigger, and a mod source registered with
    // the matrix all live here.
    this.atomBtn = makeBtn('Atom', 'btn-sm', () => this.toggleAtom());
    this.atomBtn.title = 'Show the atomic-modulation panel';

    this.testBtn = cb.onTest
      ? makeBtn('Test', 'btn-sm', () => cb.onTest?.())
      : null;

    // The controls row is a 3-column layout: left cluster (tab nav +
    // octave + test), center cluster (transport: panic / vol / meter
    // / record — moved here from the global topbar by main.ts), and
    // right cluster (BPM + MIDI picker — also moved here by main.ts).
    // Wrapping each cluster in its own container lets a CSS grid
    // align them as left / center / right reliably.
    const controlsLeft = document.createElement('div');
    controlsLeft.className = 'keyboard-controls-left';
    // Velocity + Sustain belong to the keyboard tab itself, not the
    // dock-level controls row — they only matter when the user is
    // playing the piano. They're inserted into `keyboardTabEl` below.
    controlsLeft.append(
      octCtl,
      this.kbdBtn,
      this.arpBtn,
      this.seqBtn,
      this.songBtn,
      this.atomBtn,
    );
    if (this.testBtn) controlsLeft.append(this.testBtn);

    this.controlsCenter = document.createElement('div');
    this.controlsCenter.className = 'keyboard-controls-center';

    this.controlsRight = document.createElement('div');
    this.controlsRight.className = 'keyboard-controls-right';

    controls.append(controlsLeft, this.controlsCenter, this.controlsRight);

    this.dock.appendChild(controls);

    // Keyboard tab — wraps the piano and its own controls header
    // (velocity + sustain). Mirrors the structure of the arp / seq
    // tabs which each have controls atop their working surface, so
    // velocity + sustain visually belong to the piano they apply to.
    this.keyboardTabEl = document.createElement('div');
    this.keyboardTabEl.className = 'keyboard-tab';
    const tabHeader = document.createElement('div');
    tabHeader.className = 'keyboard-tab-header';
    tabHeader.append(velCtl, this.sustainBtn);
    this.keyboardTabEl.appendChild(tabHeader);

    // Piano surface (the keys themselves)
    this.pianoEl = document.createElement('div');
    this.pianoEl.className = 'keyboard-piano';
    this.pianoEl.setAttribute('role', 'group');
    this.keyboardTabEl.appendChild(this.pianoEl);
    this.dock.appendChild(this.keyboardTabEl);

    // Arp slot — shares the piano's footprint. Only one of pianoEl /
    // arpSlot / seqSlot is visible at a time; tab toggles swap them.
    this.arpSlot = document.createElement('div');
    this.arpSlot.className = 'keyboard-arp-slot';
    this.arpSlot.hidden = true;
    this.dock.appendChild(this.arpSlot);

    // Seq slot — same shape as the arp slot. main.ts mounts the
    // SequencerPanel here once it's built.
    this.seqSlot = document.createElement('div');
    this.seqSlot.className = 'keyboard-arp-slot keyboard-seq-slot';
    this.seqSlot.hidden = true;
    this.dock.appendChild(this.seqSlot);

    // Song slot — main.ts mounts the SongPanel here. Same hide /
    // show contract as the arp + seq slots.
    this.songSlot = document.createElement('div');
    this.songSlot.className = 'keyboard-arp-slot keyboard-song-slot';
    this.songSlot.hidden = true;
    this.dock.appendChild(this.songSlot);

    // Atom slot — main.ts mounts the AtomPanel here.
    this.atomSlot = document.createElement('div');
    this.atomSlot.className = 'keyboard-arp-slot keyboard-atom-slot';
    this.atomSlot.hidden = true;
    this.dock.appendChild(this.atomSlot);

    this.element.append(this.dock);

    this.buildPiano();
    // Apply the saved active tab now that all slots exist.
    this.setActiveTab(this.activeTab);
    this.renderAll();

    // Global key capture — filter out text inputs so typing in preset name
    // fields doesn't play notes.
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
  }

  dispose(): void {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    this.panicLocal();
  }

  /** Programmatic note-on — the Test button or an external caller might use this. */
  triggerNote(note: number, velocity = this.state.velocity): void {
    this.noteOn(note, velocity);
    // Auto-release after a short time for click-to-play via Test, etc.
    window.setTimeout(() => this.noteOff(note), 350);
  }

  /** Release every note the keyboard is currently causing to sound. */
  panic(): void {
    this.panicLocal();
  }

  // ── Private: state & rendering ───────────────────────────────────────

  private setOctave(n: number): void {
    const clamped = Math.max(0, Math.min(8, n));
    if (clamped === this.state.octave) return;
    this.state.octave = clamped;
    saveState(this.state);
    this.buildPiano();
    this.renderAll();
    // Release any physically-held QWERTY keys so they don't leak across shifts.
    this.panicLocal();
  }

  /** Public so the app's global shortcuts (Ctrl+S) can fire sustain too. */
  toggleSustain(): void {
    this.toggleSustainInternal();
  }

  private toggleSustainInternal(): void {
    this.state.sustain = !this.state.sustain;
    saveState(this.state);
    this.sustainBtn.classList.toggle('active', this.state.sustain);
    if (!this.state.sustain) {
      // Releasing sustain: release any notes that are sounding but not physically held.
      for (const n of Array.from(this.sounding)) {
        if (!this.physicallyHeld.has(n)) {
          this.sounding.delete(n);
          this.cb.onNoteOff(n);
          this.refreshKey(n);
        }
      }
    }
  }

  /**
   * Swap the dock between piano view and arp view. Only the visible
   * tab takes space, so the dock footprint stays the same regardless
   * of which is active. Public so a global shortcut (Ctrl+A) can
   * toggle the tab without going through the button's click.
   */
  toggleArp(): void {
    this.setActiveTab(this.activeTab === 'arp' ? 'piano' : 'arp');
  }

  /** Mirror of `toggleArp` for the sequencer tab. */
  toggleSeq(): void {
    this.setActiveTab(this.activeTab === 'seq' ? 'piano' : 'seq');
  }

  /** Mirror of `toggleArp` for the song sequencer tab. */
  toggleSong(): void {
    this.setActiveTab(this.activeTab === 'song' ? 'piano' : 'song');
  }

  /** Mirror of `toggleArp` for the atom tab. */
  toggleAtom(): void {
    this.setActiveTab(this.activeTab === 'atom' ? 'piano' : 'atom');
  }

  private setActiveTab(tab: KeyboardTab): void {
    const changed = this.activeTab !== tab;
    this.activeTab = tab;
    this.state.activeTab = tab;
    saveState(this.state);
    // Toggle whole tab wrappers so each tab's internal controls
    // (vel + sustain for piano, etc.) hide and show together.
    this.keyboardTabEl.hidden = tab !== 'piano';
    this.arpSlot.hidden = tab !== 'arp';
    this.seqSlot.hidden = tab !== 'seq';
    this.songSlot.hidden = tab !== 'song';
    this.atomSlot.hidden = tab !== 'atom';
    this.kbdBtn.classList.toggle('active', tab === 'piano');
    this.arpBtn.classList.toggle('active', tab === 'arp');
    this.seqBtn.classList.toggle('active', tab === 'seq');
    this.songBtn.classList.toggle('active', tab === 'song');
    this.atomBtn.classList.toggle('active', tab === 'atom');
    if (changed) {
      for (const fn of this.tabListeners) fn(tab);
    }
  }

  /**
   * Subscribe to tab-change events. Fires immediately with the current
   * tab so subscribers can sync state without a separate priming call.
   * Returns an unsubscribe function.
   */
  subscribeTabChange(fn: (tab: KeyboardTab) => void): () => void {
    this.tabListeners.add(fn);
    fn(this.activeTab);
    return () => {
      this.tabListeners.delete(fn);
    };
  }

  /**
   * Install the arp panel element into the dock's arp slot. The dock
   * takes ownership of positioning it; toggling the Arp button swaps
   * the piano for this element. Pass null to remove.
   */
  setArpElement(el: HTMLElement | null): void {
    this.arpSlot.innerHTML = '';
    if (el) this.arpSlot.appendChild(el);
    // If the arp was torn down while active, fall back to piano view.
    if (!el && this.activeTab === 'arp') this.setActiveTab('piano');
  }

  /** Mirror of `setArpElement` for the sequencer tab. */
  setSeqElement(el: HTMLElement | null): void {
    this.seqSlot.innerHTML = '';
    if (el) this.seqSlot.appendChild(el);
    if (!el && this.activeTab === 'seq') this.setActiveTab('piano');
  }

  /** Mirror of `setArpElement` for the song sequencer tab. */
  setSongElement(el: HTMLElement | null): void {
    this.songSlot.innerHTML = '';
    if (el) this.songSlot.appendChild(el);
    if (!el && this.activeTab === 'song') this.setActiveTab('piano');
  }

  /** Mirror of `setArpElement` for the atom tab. */
  setAtomElement(el: HTMLElement | null): void {
    this.atomSlot.innerHTML = '';
    if (el) this.atomSlot.appendChild(el);
    if (!el && this.activeTab === 'atom') this.setActiveTab('piano');
  }

  /**
   * Append elements to the right-aligned cluster of the controls row.
   * Used by main.ts to move the BPM input, tap-tempo, metronome, and
   * MIDI picker into the same toolbar as the tab buttons. Items are
   * appended in call order; the grid layout pins the cluster to the
   * right regardless of contents.
   */
  appendToControlsRight(...items: HTMLElement[]): void {
    for (const el of items) this.controlsRight.appendChild(el);
  }

  /**
   * Append elements to the centered cluster of the controls row.
   * Used by main.ts to move the panic / volume / meter / record
   * group out of the global topbar and into the keyboard dock.
   */
  appendToControlsCenter(...items: HTMLElement[]): void {
    for (const el of items) this.controlsCenter.appendChild(el);
  }

  /**
   * Light / dim the small "on" badge on the Arp button. Call from a
   * subscribe on arp.enabled so the indicator stays in sync regardless
   * of whether the user is currently looking at the Arp tab or the
   * keyboard.
   */
  setArpEnabledIndicator(on: boolean): void {
    this.arpBadge.classList.toggle('on', on);
  }

  /** Mirror of `setArpEnabledIndicator` for the sequencer. */
  setSeqEnabledIndicator(on: boolean): void {
    this.seqBadge.classList.toggle('on', on);
  }

  /** Mirror of `setArpEnabledIndicator` for the song sequencer.
   *  Lights when the song is currently playing through its block list. */
  setSongEnabledIndicator(on: boolean): void {
    this.songBadge.classList.toggle('on', on);
  }

  private buildPiano(): void {
    this.pianoEl.innerHTML = '';
    this.keyEls.clear();

    const start = this.state.octave * 12; // MIDI C of lowest visible octave
    const end = start + VISIBLE_OCTAVES * 12;

    // Render white keys first (form the base row), then absolute-positioned blacks.
    const whites: number[] = [];
    const blacks: number[] = [];
    for (let m = start; m < end + 1; m++) {
      if (isBlackKey(m)) blacks.push(m);
      else whites.push(m);
    }

    const whiteCount = whites.length;

    for (let i = 0; i < whites.length; i++) {
      const m = whites[i]!;
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'keyboard-key white';
      el.dataset.note = String(m);
      el.style.left = `${(i / whiteCount) * 100}%`;
      el.style.width = `${100 / whiteCount}%`;
      el.title = midiToName(m);
      // Show C labels so the player can orient at a glance.
      if (m % 12 === 0) {
        const lbl = document.createElement('span');
        lbl.className = 'keyboard-key-label';
        lbl.textContent = midiToName(m);
        el.appendChild(lbl);
      }
      this.attachKeyHandlers(el, m);
      this.pianoEl.appendChild(el);
      this.keyEls.set(m, el);
    }

    for (const m of blacks) {
      // Position black keys between their neighboring whites. A black key at
      // MIDI m sits between the white at m-1 and the white at m+1.
      const prevWhiteIdx = whites.indexOf(m - 1);
      if (prevWhiteIdx < 0) continue;
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'keyboard-key black';
      el.dataset.note = String(m);
      // Center the black key on the boundary between prev and next white.
      el.style.left = `${((prevWhiteIdx + 1) / whiteCount) * 100}%`;
      el.style.width = `${(100 / whiteCount) * 0.6}%`;
      el.title = midiToName(m);
      this.attachKeyHandlers(el, m);
      this.pianoEl.appendChild(el);
      this.keyEls.set(m, el);
    }
  }

  private attachKeyHandlers(el: HTMLElement, note: number): void {
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      this.noteOn(note, this.state.velocity);
    });
    el.addEventListener('pointerup', (e) => {
      e.preventDefault();
      this.noteOff(note);
    });
    el.addEventListener('pointercancel', () => this.noteOff(note));
    el.addEventListener('pointerleave', (e) => {
      if ((e.buttons & 1) === 0) this.noteOff(note);
    });
  }

  private renderAll(): void {
    this.octaveLabel.textContent = `C${this.state.octave}`;
    this.sustainBtn.classList.toggle('active', this.state.sustain);
    for (const n of this.keyEls.keys()) this.refreshKey(n);
  }

  private refreshKey(note: number): void {
    const el = this.keyEls.get(note);
    if (!el) return;
    el.classList.toggle('held', this.physicallyHeld.has(note));
    el.classList.toggle('sounding', this.sounding.has(note));
  }

  // ── Private: note routing ────────────────────────────────────────────

  private noteOn(note: number, velocity: number): void {
    if (this.sounding.has(note) && this.physicallyHeld.has(note)) return;
    this.physicallyHeld.add(note);
    if (!this.sounding.has(note)) {
      this.sounding.add(note);
      this.cb.onNoteOn(note, velocity);
    }
    this.refreshKey(note);
  }

  private noteOff(note: number): void {
    if (!this.physicallyHeld.has(note)) return;
    this.physicallyHeld.delete(note);
    if (!this.state.sustain && this.sounding.has(note)) {
      this.sounding.delete(note);
      this.cb.onNoteOff(note);
    }
    this.refreshKey(note);
  }

  private panicLocal(): void {
    for (const n of Array.from(this.sounding)) this.cb.onNoteOff(n);
    this.sounding.clear();
    this.physicallyHeld.clear();
    this.keysHeld.clear();
    for (const n of this.keyEls.keys()) this.refreshKey(n);
  }

  // ── QWERTY handlers ──────────────────────────────────────────────────

  private onKeyDown = (e: KeyboardEvent): void => {
    // A focused knob or similar has already handled this and called
    // preventDefault — don't steal its arrow keys etc.
    if (e.defaultPrevented) return;
    if (shouldIgnoreKeyEvent(e)) return;

    // Pitch bend via arrow keys. Releases snap back to 0 (see onKeyUp).
    if (e.code === 'ArrowUp') {
      if (e.repeat) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      this.cb.onPitchBend?.(1);
      return;
    }
    if (e.code === 'ArrowDown') {
      if (e.repeat) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      this.cb.onPitchBend?.(-1);
      return;
    }

    // Octave shift — use ',' '.' in Ableton style, but those codes are also
    // notes in the current map (Comma=12, Period=14). Use bracket keys instead.
    if (e.code === 'BracketLeft') {
      e.preventDefault();
      this.setOctave(this.state.octave - 1);
      return;
    }
    if (e.code === 'BracketRight') {
      e.preventDefault();
      this.setOctave(this.state.octave + 1);
      return;
    }

    const offset = QWERTY_MAP[e.code];
    if (offset === undefined) return;
    if (this.keysHeld.has(e.code)) return; // ignore auto-repeat
    this.keysHeld.add(e.code);
    e.preventDefault();

    const midi = this.state.octave * 12 + offset;
    this.noteOn(midi, this.state.velocity);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
      this.cb.onPitchBend?.(0);
      return;
    }
    if (!this.keysHeld.has(e.code)) return;
    const offset = QWERTY_MAP[e.code];
    if (offset === undefined) return;
    this.keysHeld.delete(e.code);
    const midi = this.state.octave * 12 + offset;
    this.noteOff(midi);
  };
}

// ── Helpers ──────────────────────────────────────────────────────────

function makeBtn(
  label: string,
  extraClass: string,
  handler: (e: Event) => void,
): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = `btn ${extraClass}`;
  b.textContent = label;
  b.addEventListener('click', handler);
  return b;
}

/**
 * Should a keydown be swallowed (i.e. not played as a note)?
 *
 * Block for any input where the user is typing characters that overlap
 * the QWERTY note map. Text inputs are obvious; `type="number"` also
 * counts because the upper-row note map includes digits (2 3 5 6 7 9 0
 * for black keys), so typing a number into a Length / BPM field
 * would otherwise play a note simultaneously. Selects, range sliders,
 * and our knob (role=slider) stay passthrough so the user can keep
 * playing while adjusting those parameters.
 */
function shouldIgnoreKeyEvent(e: KeyboardEvent): boolean {
  // Don't swallow modifier-chorded shortcuts (Cmd+S, Ctrl+Z, etc.).
  if (e.metaKey || e.ctrlKey || e.altKey) return true;
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const input = t as HTMLInputElement;
    // Default type is 'text' (empty string when unset).
    const type = (input.type || 'text').toLowerCase();
    const TEXT_TYPES = new Set([
      'text',
      'email',
      'password',
      'search',
      'url',
      'tel',
      // Number inputs receive digits, which collide with the
      // upper-row QWERTY piano map. Treat them as text so focus
      // means "I'm typing", not "I'm performing".
      'number',
    ]);
    return TEXT_TYPES.has(type);
  }
  return false;
}

function loadState(): KeyboardState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as Partial<KeyboardState> & {
      // The pre-tab schema persisted a `mode: 'expanded' | 'strip' | 'hidden'`
      // field; it's gone now (dock is always expanded). We just ignore it.
      mode?: string;
    };
    const validTabs: KeyboardTab[] = ['piano', 'arp', 'seq'];
    const activeTab =
      typeof parsed.activeTab === 'string' &&
      validTabs.includes(parsed.activeTab as KeyboardTab)
        ? (parsed.activeTab as KeyboardTab)
        : DEFAULT_STATE.activeTab;
    return {
      activeTab,
      octave:
        typeof parsed.octave === 'number' && Number.isFinite(parsed.octave)
          ? Math.max(0, Math.min(8, Math.floor(parsed.octave)))
          : DEFAULT_STATE.octave,
      velocity:
        typeof parsed.velocity === 'number' && Number.isFinite(parsed.velocity)
          ? Math.max(0, Math.min(1, parsed.velocity))
          : DEFAULT_STATE.velocity,
      sustain: !!parsed.sustain,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function saveState(state: KeyboardState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota / privacy mode — ignore
  }
}

// Force NOTE_NAMES to be referenced so tree-shaking doesn't discard the
// re-export in tests that import by side effect. (No-op at runtime.)
void NOTE_NAMES;
